import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { ROUTES } from '@/shared/config/routes';
import { TOOL_CATEGORIES } from '@/shared/constants/category-colors';

import { useManagedTools } from '../useManagedTools';
import { useSpringboardTools } from '../useSpringboardTools';
import type { ToolCatalogEntry } from '@/shared/types/tool-catalog.types';
import type { UseToolsCatalogOptions, UseToolsCatalogResult } from './useToolsCatalog.types';

/**
 * Every tool a workspace can reach, in one flat list.
 *
 * The category grids each ask for their own slice; the redesigned tools page
 * shows them all at once behind a search box and two filters, so it needs the
 * whole catalog and the category as a field rather than as a container.
 *
 * Within a category the platform's own tools come first, then the registry's,
 * which is the order the grids already established.
 */
export const useToolsCatalog = ({
  showFlowDigest,
  workspaceSlug,
  onLaunchAgent,
}: UseToolsCatalogOptions = {}): UseToolsCatalogResult => {
  const { t } = useTranslation('springboard');
  const { t: tCommon } = useTranslation('common');

  const navigate = useNavigate();
  const workspaceId = useCurrentWorkspaceId();
  const { canManageTools } = usePermissions();

  const { tools: managedTools, isLoading: isManagedLoading } = useManagedTools({
    showFlowDigest,
    workspaceSlug,
  });

  // One query per category, because that is the shape the agents endpoint is
  // tagged by. Rules of hooks is why these are spelled out rather than mapped.
  const enabled = !!workspaceId;
  const plan = useSpringboardTools({ tabId: 'plan', enabled });
  const create = useSpringboardTools({ tabId: 'create', enabled });
  const scale = useSpringboardTools({ tabId: 'scale', enabled });
  const deliver = useSpringboardTools({ tabId: 'deliver', enabled });
  const registryByCategory = { plan, create, scale, deliver };

  const tools: ToolCatalogEntry[] = [];

  for (const category of TOOL_CATEGORIES) {
    for (const tool of managedTools) {
      if (tool.category !== category) continue;
      tools.push({
        id: tool.id,
        category,
        title: tool.title,
        description: tool.description,
        kind: tool.kind,
        disabled: false,
        onOpen: tool.onOpen,
      });
    }

    for (const tool of registryByCategory[category].tools) {
      // An admin looking at an unconfigured tool gets a way in — to its
      // configuration rather than to the tool, which is not ready to run.
      const canConfigure = tool.isUnconfigured && canManageTools;
      const isDisabled = tool.disabled && !canConfigure;

      tools.push({
        id: tool.id,
        category,
        title: tool.title,
        description: tool.description,
        kind: 'agent',
        ...(tool.visibility ? { visibility: tool.visibility } : {}),
        disabled: isDisabled,
        action: canConfigure ? 'configure' : undefined,
        badgeLabel: canConfigure
          ? t('springboard.toolGridV4.notConfigured')
          : isDisabled
            ? (tool.disabledReason ?? tCommon('components.toolCard.comingSoon'))
            : undefined,
        disabledTooltip:
          isDisabled && tool.isUnconfigured
            ? t('springboard.toolGridV4.thisToolNeedsToBe')
            : undefined,
        onOpen: canConfigure
          ? () => void navigate(ROUTES.ADMIN_AGENT_CONFIG(tool.id))
          : () => onLaunchAgent?.(tool.id),
      });
    }
  }

  return {
    tools,
    isLoading:
      isManagedLoading ||
      plan.isLoading ||
      create.isLoading ||
      scale.isLoading ||
      deliver.isLoading,
  };
};
